import * as React from 'react';
import * as _ from 'lodash';
import Table from '@material-ui/core/Table';
import TableHead from '@material-ui/core/TableHead';
import TableRow, {ExtendedTableRowProps} from '@material-ui/core/TableRow';
import TableCell, {TableCellProps} from '@material-ui/core/TableCell';
import TableBody from '@material-ui/core/TableBody';
import {AutoSizer, Grid, GridCellRenderer, ScrollSync, defaultCellRangeRenderer} from 'react-virtualized';
import {GridCoreProps} from 'react-virtualized/dist/es/Grid';
import * as seedrandom from 'seedrandom';
import {withStyles} from '@material-ui/core/styles';
import {WithStyles} from '@material-ui/core/styles/withStyles';
import Tooltip from '@material-ui/core/Tooltip';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Resizable from 're-resizable';
import {findDOMNode} from 'react-dom';
import * as classNames from 'classnames';
import EditableTableCell, {EditableTableCellProps, useRowHoverStyles} from './EditableTableCell';
import Img, {IMG_SIZES} from '../../shared/components/Img/Img';
import {
  ColumnsInfo,
  isAllSelected,
  isAnySelected,
  isSelected,
  RowDataType,
  RowStatus,
  TableState,
} from './redux/actions';
import Checkbox from '@material-ui/core/Checkbox/Checkbox';
import TableFilter from './TableFilter/TableFilter';
import {VirtualizedComponentProps} from './consts';
import getScrollbarWidth from '../../shared/vendor/react-custom-scrollbars/src/utils/getScrollbarWidth';
import {
  renderCellContent,
  getColumnDisplayName,
  getSelectionByIndices,
  getValidateValue,
  isNumericType,
  isChildRow,
  isParentRow,
  getParentRow,
  getStickyColumnsNames,
} from './VirtualizedHelpers';
import {getImageFallbackComponent} from '../../MyAccount/ViewDesigns/helpers/resizeImageUrlHelper';
import ErrorIcon from '@material-ui/icons/Error';
import {getColumnsWithErrors} from './redux/selectors';
import {hoverContainerStyles} from '../../shared/components/InlineButton';
import {Omit} from '@material-ui/core';
import {fade, lighten} from '@material-ui/core/styles/colorManipulator';
import SubdirectoryArrowRight from 'mdi-material-ui/SubdirectoryArrowRight';

import memoize from 'memoize-one';
import {useSelector, shallowEqual} from 'react-redux';
import {stringToDashed} from 'cl-automation-utils';

const MIN_COLUMN_WIDTH = 176; // 8(spacing unit) * 22;

export interface ColumnsWidth {
  [columnName: string]: number;
}

// This is an exception, hard coded class key should be avoided
type VirtualizedTableClassKey =
  | 'root'
  | 'rootResizing'
  | 'rootVirtualized'
  | 'tableRow'
  | 'tableRowVirtualized'
  | 'tableCell'
  | 'tableCellVariableWidth'
  | 'tableCellFixed'
  | 'lastTableCellInRow'
  | 'tableHeadScrollPadding'
  | 'tableHead'
  | 'tableHeadRow'
  | 'resizingColumn'
  | 'tableBody'
  | 'grid'
  | 'skeleton'
  | 'resizeHandle'
  | 'headerContainer'
  | 'visibleOnRowHover'
  | 'childBackground'
  | 'parentShadow'
  | 'childColumnWidth'
  | 'cellIconSpacing'
  | keyof typeof hoverContainerStyles;

type VirtualizedTablePropsWithStyles<RowData extends RowDataType> = VirtualizedComponentProps<RowData> &
  WithStyles<VirtualizedTableClassKey>;

function getAlign(columnInfo: ColumnsInfo) {
  return columnInfo.align || (isNumericType(columnInfo.type) && !columnInfo.options ? 'right' : 'left'); // TODO once 'select' type is used in publish setup tables columnInfo.options isn't needed
}

class VirtualizedTable<RowData extends RowDataType> extends React.PureComponent<
  VirtualizedTablePropsWithStyles<RowData>
> {
  state = {
    columnsWidth: {} as ColumnsWidth,
    measured: false,
    resizingColumn: null as null | string,
  };
  private virtualizedComponentRef: Grid;
  private originalColumnsWidth: ColumnsWidth;

  componentWillMount() {
    this.loadColumnsWidth(this.props);
  }

  componentDidMount() {
    this.setState({columnsWidth: this.getColumnsWidth(), measured: true});
    if (this.virtualizedComponentRef) {
      this.virtualizedComponentRef.forceUpdate();
    }
  }

  componentWillReceiveProps(nextProps) {
    if (
      nextProps.visibleColumns !== this.props.visibleColumns ||
      (!this.props.count && nextProps.count > 0) ||
      nextProps.columns !== this.props.columns
    ) {
      this.loadColumnsWidth(nextProps);
      this.setState({measured: false});
      this.getMemoizeResizableCellComponent.cache.clear();
    }
    if (this.props.data.count() === 0 && nextProps.data.count() > 0) {
      if (this.virtualizedComponentRef) {
        this.virtualizedComponentRef.forceUpdate();
      }
    }
  }

  componentDidUpdate() {
    const {measured} = this.state;
    if (!measured) {
      window.requestAnimationFrame(() => {
        this.componentDidMount();
      });
    }
  }

  private static getPersistenceKey(props) {
    const {visibleColumns, persistenceKey} = props;
    return `${persistenceKey}ColumnsWidth-${visibleColumns.join('-')}`;
  }

  private loadColumnsWidth(props) {
    const {persistenceKey} = props;
    if (persistenceKey) {
      const storedColumnsWidth = window.localStorage.getItem(VirtualizedTable.getPersistenceKey(props));
      if (storedColumnsWidth) {
        this.setState({columnsWidth: JSON.parse(storedColumnsWidth)});
      }
    }
  }

  private getColumnsWidth = () => {
    let visibleColumns = this.getAllVisibleColumns(this.props.visibleColumns, this.props.columns);
    return _.mapValues(_.mapKeys(visibleColumns), (columnInfo, columnName) => {
      const ref = this[columnName];
      if (ref) {
        const node = findDOMNode(ref) as HTMLElement;
        return node.offsetWidth;
      }
    });
  };

  private getAllVisibleColumns = memoize((visibleColumns: TableState['visibleColumns'], columns) => {
    const {
      thumbnail,
      selectable,
      supportChildren,
      columnsConfig: {status: statusColumn},
    } = this.props;

    const stickyColumns = getStickyColumnsNames(columns);
    if (stickyColumns) {
      visibleColumns = _.concat(stickyColumns, _.difference(visibleColumns, stickyColumns));
    }
    if (thumbnail) {
      visibleColumns = ['thumbnail', ...visibleColumns];
    }
    if (statusColumn) {
      visibleColumns = ['status', ...visibleColumns];
    }
    if (selectable) {
      visibleColumns = ['checkbox', ...visibleColumns];
    }
    if (supportChildren) {
      visibleColumns = ['child', ...visibleColumns];
    }
    return visibleColumns;
  });

  createSortHandler = columnName => () => {
    const {onRequestSort, sortProps} = this.props;
    onRequestSort({[columnName]: sortProps && sortProps[columnName] === 'asc' ? 'desc' : 'asc'});
  };

  cellRenderer: GridCellRenderer | any = ({columnIndex, rowIndex, style, key}) => {
    const {resizingColumn} = this.state;
    const rowData = this.props.data.get(rowIndex);
    const selected = isSelected(this.props, rowIndex);
    const {columns, visibleColumns} = this.props;
    const columnName = this.getAllVisibleColumns(this.props.visibleColumns, columns)[columnIndex];
    const CellComponent = this.CellComponent;
    return (
      <CellComponent
        key={key}
        data-row-index={rowIndex}
        columns={columns}
        columnName={columnName}
        rowData={rowData}
        resizingColumn={resizingColumn}
        rowIndex={rowIndex}
        style={this.getMemoizedStyle(style)}
        visibleColumns={visibleColumns}
        selected={selected}
      />
    );
  };

  private getMemoizedStyle = _.memoize(
    (style: React.CSSProperties): React.CSSProperties => {
      return style;
    },
    ({top, left, width, height} = {}) => `${top}-${left}=${width}-${height}`
  );

  private CellComponent = React.memo(
    ({
      columns,
      columnName,
      rowData,
      resizingColumn,
      rowIndex,
      visibleColumns,
      style,
      selected,
    }: Pick<TableState, 'columns' | 'visibleColumns'> & {
      'data-row-index': number;
      rowData: RowData;
      columnName: string;
      resizingColumn: string;
      rowIndex: number;
      style: React.CSSProperties;
      selected: boolean;
    }) => {
      const {
        data,
        onChange,
        virtualized,
        onToggleSelect,
        persistenceKey,
        columnsConfig: {status: statusColumn, thumbnail: thumbnailColumnName, title},
        classes: {
          inlineButtonHoverContainer,
          tableCell,
          skeleton,
          lastTableCellInRow,
          resizingColumn: resizingColumnClass,
          tableCellFixed,
          tableCellVariableWidth,
          childColumnWidth,
          cellIconSpacing,
          ...otherClasses
        },
      } = this.props;
      let {id} = this.props;
      const isChild = isChildRow(rowData);
      const parentTitle = isChild && _.get(getParentRow(rowData, data), title);
      id = id ? id : `cla-${stringToDashed(persistenceKey)}-cell`;
      const parentChildId = isChild ? 'child' : 'parent';

      switch (columnName) {
        case 'checkbox':
          return (
            <TableCell style={style} component="div" className={tableCell}>
              <Checkbox
                id={`${id}-select-row-${rowIndex}`}
                checked={selected}
                onChange={() => onToggleSelect(rowIndex)}
              />
            </TableCell>
          );
        case 'status':
          const status: Partial<RowStatus> = useSelector(
            () => (statusColumn && rowData ? statusColumn(rowData, this.props) : {}),
            shallowEqual
          );
          return (
            <TableCell id={`${id}-status-row-${rowIndex}`} style={style} component="div" className={tableCell}>
              {status.icon}
            </TableCell>
          );
        case 'thumbnail':
          return (
            <TableCell id={`${id}-thumbnail-row-${rowIndex}`} style={style} component="div" className={tableCell}>
              <Img
                className="cla-img"
                size={IMG_SIZES.Thumbnail}
                src={rowData ? (rowData[thumbnailColumnName] as string) : undefined}
                unloader={isChild ? undefined : getImageFallbackComponent(48)}
              />
            </TableCell>
          );
        case 'child':
          return (
            <TableCell
              id={`${id}-${parentChildId}-${rowIndex}`}
              component="div"
              className={classNames(tableCell, childColumnWidth)}
            >
              {isChild ? (
                <Tooltip className="cla-tooltip" title={parentTitle ? 'Variation of ' + parentTitle : 'Variation'}>
                  <SubdirectoryArrowRight className="cla-child-icon" fontSize="small" />
                </Tooltip>
              ) : (
                undefined
              )}
            </TableCell>
          );
        default:
          const lastColumnName = _.last(visibleColumns);
          const lastColumn = lastColumnName === columnName;
          const columnsInfo = columns[columnName] as ColumnsInfo;
          const cellComponentProps: Partial<EditableTableCellProps> = {};
          const disabled = rowData && _.isFunction(columnsInfo.disabled) && columnsInfo.disabled(rowData, data);
          const editableCell = rowData && onChange && !(disabled || columnsInfo.readOnly);
          const CellComponent: any = editableCell
            ? EditableTableCell
            : (TableCell as React.ComponentClass<Omit<TableCellProps, 'onChange'>>);
          cellComponentProps.align = getAlign(columnsInfo);
          if (editableCell) {
            if (rowData) {
              cellComponentProps.validate = !disabled && getValidateValue(rowData, columnName, columnsInfo, data);
              cellComponentProps.value = rowData[columnName];
            }
            cellComponentProps.highlightContentOnHover = columnsInfo.getHref ? 'edit-button' : 'row';
            cellComponentProps.name = columnName;
            cellComponentProps.onChange = value =>
              onChange(getSelectionByIndices([rowIndex], data, this.props.columnsConfig), columnName, value);
          }
          const displayName = getColumnDisplayName(columnName, columns[columnName]);
          return (
            <CellComponent
              id={`${id}-${stringToDashed(displayName.toLowerCase())}-row-${rowIndex}`}
              component="div"
              className={classNames({
                [tableCell]: true,
                [tableCellVariableWidth]: true,
                [lastTableCellInRow]: lastColumn,
                [resizingColumnClass]: columnName === resizingColumn,
                [tableCellFixed]: !virtualized,
              })}
              style={{
                cursor: disabled || columnsInfo.readOnly ? 'default' : undefined,
                ...style,
              }}
              {...cellComponentProps}
            >
              {rowData && !disabled && columnsInfo.icon
                ? React.cloneElement(columnsInfo.icon(rowData), {className: cellIconSpacing})
                : null}
              {rowData ? (
                disabled || columnsInfo.iconOnly ? null : (
                  renderCellContent(rowData, columnName, columnsInfo, otherClasses)
                )
              ) : (
                <div
                  className={skeleton}
                  style={{
                    width: 50 + seedrandom('table' + rowIndex + columnName)() * 100,
                  }}
                />
              )}
            </CellComponent>
          );
      }
    }
  );

  private handleResizeStart = columnName => {
    this.originalColumnsWidth = this.state.columnsWidth;
    this.setState({resizingColumn: columnName});
  };

  private handleResize = (columnName, e, d, r, delta) => {
    window.requestAnimationFrame(() => {
      const columnsWidth = {
        ...this.originalColumnsWidth,
        [columnName]: Math.max(this.originalColumnsWidth[columnName] + delta.width, MIN_COLUMN_WIDTH),
      };
      this.setState({columnsWidth});
      this.recomputeGridSize(columnName);
    });
  };

  private recomputeGridSize(columnName) {
    if (this.virtualizedComponentRef) {
      this.virtualizedComponentRef.recomputeGridSize({
        columnIndex: _.indexOf(this.getAllVisibleColumns(this.props.visibleColumns, this.props.columns), columnName),
      });
    }
  }

  private handleResizeStop = () => {
    const {persistenceKey} = this.props;
    const columnsWidth = this.getColumnsWidth();
    if (persistenceKey) {
      window.localStorage.setItem(VirtualizedTable.getPersistenceKey(this.props), JSON.stringify(columnsWidth));
    }
    this.setState({resizingColumn: null, columnsWidth});
  };

  private handleSectionRendered: GridCoreProps['onSectionRendered'] = ({rowStartIndex, rowStopIndex}) => {
    const {onRowsRendered} = this.props;
    if (onRowsRendered) {
      onRowsRendered({startIndex: rowStartIndex, stopIndex: rowStopIndex});
    }
  };

  private getMemoizeResizableCellComponent = _.memoize((columnName, lastColumn) => props => {
    const {
      classes: {resizeHandle},
    } = this.props;
    const {columnsWidth, measured} = this.state;
    return (
      <Resizable
        handleClasses={{right: resizeHandle}}
        handleComponent={{right: 'div'}}
        onResizeStart={this.handleResizeStart.bind(this, columnName)}
        onResize={this.handleResize.bind(this, columnName)}
        onResizeStop={this.handleResizeStop}
        size={measured && columnsWidth[columnName] ? {width: columnsWidth[columnName]} : undefined}
        ref={ref => {
          this[columnName] = ref;
        }}
        {...props}
        enable={lastColumn ? {} : {right: true}}
      />
    );
  });

  getColumnWidth = ({index}) => {
    const {columnsWidth} = this.state;
    return columnsWidth[this.getAllVisibleColumns(this.props.visibleColumns, this.props.columns)[index]];
  };

  getTableWidth = memoize((columnsWidth: ColumnsWidth) => {
    return _.reduce(columnsWidth, (totalWidth, width) => totalWidth + width, 0);
  });

  Row = ({rowIndex, children, style}: {rowIndex: number; children: any; style?: ExtendedTableRowProps['style']}) => {
    const rowHoverStyles = useRowHoverStyles(undefined);
    const {
      columnsConfig: {status: statusColumn},
      virtualized,
      data,
      classes: {inlineButtonHoverContainer, tableRow, tableRowVirtualized, childBackground, parentShadow},
    } = this.props;
    const rowData = data.get(rowIndex);
    const status: Partial<RowStatus> = statusColumn && rowData ? statusColumn(rowData, this.props) : {};
    const selected = isSelected(this.props, rowIndex);
    const isChild = isChildRow(rowData);
    const isParent = isParentRow(rowData);
    return (
      <TableRow
        component="div"
        statusColor={status.color}
        key={rowIndex}
        hover
        selected={selected}
        style={style ? {...style, width: this.getTableWidth(this.state.columnsWidth)} : undefined}
        className={classNames(tableRow, inlineButtonHoverContainer, rowHoverStyles.tableRow, {
          [tableRowVirtualized]: virtualized,
          [childBackground]: isChild,
          [parentShadow]: isParent,
        })}
      >
        {children}
      </TableRow>
    );
  };

  HeadRowContent = React.memo(
    ({
      visibleColumns,
      columnsWidth,
      columns,
    }: Pick<TableState, 'visibleColumns' | 'columns' | 'data' | 'selection' | 'count'> & {
      columnsWidth: ColumnsWidth;
    }) => {
      const {
        selectable,
        thumbnail,
        supportChildren,
        onToggleSelectAll,
        sortProps,
        onRequestSort,
        virtualized,
        persistenceKey,
        classes: {
          resizingColumn: resizingColumnClass,
          tableCell,
          lastTableCellInRow,
          headerContainer,
          tableCellFixed,
          tableCellVariableWidth,
        },
        columnsConfig: {status: statusColumn},
      } = this.props;
      let {id} = this.props;
      const lastColumnName = _.last(visibleColumns);
      const {measured, resizingColumn} = this.state;
      const columnsWithErrors = getColumnsWithErrors(this.props as TableState<any>);
      id = id ? id : `cla-${stringToDashed(persistenceKey)}-head`;
      return (
        <React.Fragment>
          {selectable ? (
            <TableCell
              className={tableCell}
              component={props => <div ref={ref => (this['checkbox'] = ref)} {...props} />}
            >
              <Checkbox
                id={`${id}-select-all`}
                checked={isAllSelected(this.props)}
                indeterminate={isAnySelected(this.props) && !isAllSelected(this.props)}
                onChange={() => onToggleSelectAll()}
              />
            </TableCell>
          ) : (
            undefined
          )}
          {supportChildren ? (
            <TableCell
              id={`${id}-parent-child`}
              className={tableCell}
              component={props => <div ref={ref => (this['child'] = ref)} {...props} />}
            />
          ) : (
            undefined
          )}
          {statusColumn ? (
            <TableCell
              id={`${id}-status`}
              className={tableCell}
              component={props => <div ref={ref => (this['status'] = ref)} {...props} />}
            >
              Status
            </TableCell>
          ) : (
            undefined
          )}
          {thumbnail ? (
            <TableCell
              id={`${id}-thumbnail`}
              className={tableCell}
              component={props => <div ref={ref => (this['thumbnail'] = ref)} {...props} />}
            >
              <Img className="cla-img" size={IMG_SIZES.Thumbnail} />
            </TableCell>
          ) : (
            undefined
          )}
          {_.map(visibleColumns, columnName => {
            const lastColumn = lastColumnName === columnName;
            const displayName = getColumnDisplayName(columnName, columns[columnName]);
            return (
              <TableCell
                id={`${id}-${stringToDashed(displayName.toLowerCase())}`}
                {...virtualized && {component: this.getMemoizeResizableCellComponent(columnName, lastColumn)}}
                className={classNames({
                  [tableCell]: true,
                  [tableCellVariableWidth]: true,
                  [lastTableCellInRow]: lastColumn,
                  [resizingColumnClass]: resizingColumn === columnName,
                  [tableCellFixed]: !virtualized,
                })}
                key={columnName}
                style={
                  measured
                    ? {width: columnsWidth[columnName]}
                    : {
                        minWidth: columnsWidth[columnName],
                        flexGrow: 1,
                      }
                }
              >
                <div className={headerContainer}>
                  {columnsWithErrors[columnName] ? <ErrorIcon className="cla-error-icon" color="error" /> : undefined}
                  {columns[columnName].infoIcon}
                  {onRequestSort && !columns[columnName].sortDisabled ? (
                    <Tooltip title={`Sort by ${displayName}`}>
                      <TableSortLabel
                        className="cla-sort"
                        active={sortProps ? !!sortProps[columnName] : false}
                        direction={sortProps ? sortProps[columnName] : undefined}
                        onClick={this.createSortHandler(columnName)}
                      >
                        {displayName}
                      </TableSortLabel>
                    </Tooltip>
                  ) : (
                    <span>{displayName}</span>
                  )}
                </div>
                {columns[columnName].actionComponent
                  ? React.createElement(columns[columnName].actionComponent, {
                      columnName,
                      tableName: persistenceKey,
                    })
                  : undefined}
              </TableCell>
            );
          })}
        </React.Fragment>
      );
    }
  );

  cellRangeRenderer: GridCoreProps['cellRangeRenderer'] = params => {
    const renderedCells = defaultCellRangeRenderer(params);
    const Row = this.Row;
    return _.map(_.groupBy(renderedCells, 'props.data-row-index'), (cells, dataRowIndex) => {
      const rowIndex = parseInt(dataRowIndex, 10);
      const top = _.first(cells)['props'].style.top;
      const {columnsWidth} = this.state;
      return (
        <Row
          rowIndex={rowIndex}
          key={rowIndex}
          style={{transform: `translateY(${top}px)`, width: this.getTableWidth(columnsWidth)}}
        >
          {cells.map((cell: any) => {
            return React.cloneElement(cell, {style: this.getMemoizedStyle({...cell.props.style, top: 0})});
          })}
        </Row>
      );
    });
  };

  renderTable(scrollLeft, onScroll) {
    const {
      selectable,
      thumbnail,
      getVisibleColumnsPresets,
      onVisibleColumnsChange,
      visibleColumns,
      filter,
      filterProps,
      onToggleSelectAll,
      noContentRenderer,
      onChange,
      sortProps,
      onRequestSort,
      columns,
      count,
      virtualizedComponentRef,
      virtualized,
      data,
      selection,
      persistenceKey,
      classes: {tableHeadScrollPadding, tableHeadRow, tableHead, root, rootResizing, rootVirtualized, tableBody, grid},
      ...otherProps
    } = this.props;
    const {columnsWidth, measured, resizingColumn} = this.state;
    const HeadRowContent = this.HeadRowContent;
    return (
      <Table
        component="div"
        className={classNames(root, {[rootVirtualized]: virtualized, [rootResizing]: !!resizingColumn})}
      >
        <TableHead component="div" className={classNames(tableHead, {[tableHeadScrollPadding]: virtualized})}>
          <TableRow component="div" className={tableHeadRow} style={{transform: `translateX(-${scrollLeft}px)`}}>
            <HeadRowContent
              data={data}
              columnsWidth={columnsWidth}
              columns={columns}
              visibleColumns={_.concat(getStickyColumnsNames(columns), visibleColumns)}
              selection={selection}
              count={count}
            />
          </TableRow>
        </TableHead>
        <TableBody component="div" className={tableBody}>
          {virtualized ? (
            <AutoSizer>
              {({width, height}) => {
                if (!measured) {
                  return null;
                }
                return (
                  <Grid
                    ref={ref => {
                      this.virtualizedComponentRef = ref;
                      if (virtualizedComponentRef) {
                        virtualizedComponentRef(ref);
                      }
                    }}
                    className={grid}
                    height={height}
                    rowCount={count || 0}
                    rowHeight={48 + 16}
                    cellRangeRenderer={this.cellRangeRenderer}
                    columnWidth={this.getColumnWidth}
                    width={width}
                    columnCount={_.size(this.getAllVisibleColumns(this.props.visibleColumns, columns))}
                    cellRenderer={this.cellRenderer}
                    {...otherProps}
                    onSectionRendered={this.handleSectionRendered}
                    onScroll={onScroll}
                  />
                );
              }}
            </AutoSizer>
          ) : (
            data
              .map((rowData, rowIndex) => {
                const Row = this.Row;
                return (
                  <Row key={rowIndex} rowIndex={rowIndex}>
                    {this.getAllVisibleColumns(this.props.visibleColumns, columns).map((columnName, columnIndex) =>
                      this.cellRenderer({
                        rowIndex,
                        columnIndex,
                        key: `${columnIndex}-${rowIndex}`,
                      })
                    )}
                  </Row>
                );
              })
              .valueSeq()
          )}
        </TableBody>
      </Table>
    );
  }

  render() {
    const {filter, filterProps, noContentRenderer, columns, count, virtualized} = this.props;
    let content;
    switch (count) {
      case undefined:
        break;
      case 0:
        content = noContentRenderer(this.props);
        break;
      default:
        content = virtualized ? (
          <ScrollSync>{({scrollLeft, onScroll}) => this.renderTable(scrollLeft, onScroll)}</ScrollSync>
        ) : (
          this.renderTable(0, undefined)
        );
    }
    return (
      <React.Fragment>
        {virtualized && <TableFilter count={count} columns={columns} filterValues={filter} {...filterProps} />}
        {content}
      </React.Fragment>
    );
  }
}

export default withStyles<VirtualizedTableClassKey, {}>(theme => ({
  root: {
    minWidth: '100%',
    flexGrow: 1,
    flexDirection: 'column',
    '& .ReactVirtualized__Grid__innerScrollContainer': {
      overflow: 'visible!important',
    },
  },
  rootResizing: {
    '& $tableRow': {
      backgroundColor: 'transparent!important',
      pointerEvents: 'none',
    },
  },
  rootVirtualized: {
    display: 'flex',
  },
  tableHeadScrollPadding: {
    paddingRight: getScrollbarWidth(),
  },
  tableHeadRow: {
    display: 'flex',
    alignItems: 'stretch',
    height: 'auto',
  },
  tableHead: {
    borderBottom: `1px solid ${lighten(fade(theme.palette.divider, 1), 0.88)}`,
    '& $tableCell': {
      borderBottom: 'none',
    },
    zIndex: 2,
  },
  tableBody: {
    flexGrow: 1,
  },
  tableRow: {
    display: 'flex',
    left: 0,
    right: 0,
    alignItems: 'stretch',
    height: theme.spacing(8),
  },
  tableRowVirtualized: {
    position: 'absolute' as 'absolute',
  },
  grid: {
    overflowY: 'auto!important' as 'auto',
    overflowX: 'auto!important' as 'auto',
  },
  tableCell: {
    display: 'flex',
    alignItems: 'center',
    flexGrow: 0,
    flexShrink: 0,
  },
  tableCellVariableWidth: {
    minWidth: MIN_COLUMN_WIDTH,
    '$tableHeadRow &': {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
    '& > *': {
      maxWidth: '100%',
    },
    '& $tableCellContent, $tableHeadRow & span': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      display: 'inline',
    },
  },
  tableCellContent: {},
  tableCellFixed: {
    flexBasis: 0, // necessary for alignment because disabled ResizableCell
  },
  lastTableCellInRow: {
    flexGrow: '1!important' as React.CSSProperties['flexGrow'],
  },
  resizingColumn: {},
  resizeHandle: {
    opacity: 0,
    transition: theme.transitions.create('opacity', {duration: 75}),
    '$tableHeadRow:hover &': {
      opacity: 0.75,
    },
    '$resizingColumn &, &:hover': {
      opacity: 1,
    },
    zIndex: 1,
    '& div': {
      transition: theme.transitions.create(['height', 'width'], {duration: 75}),
      backgroundColor: theme.palette.primary.main,
      width: 1,
      height: '100%',
    },
    '$resizingColumn & div, &:hover div': {
      height: '100vh',
      width: 2,
    },
  },
  [`@keyframes fadeInAndOut`]: {
    from: {opacity: 0.3},
    to: {opacity: 0.6},
  } as React.CSSProperties,
  skeleton: {
    backgroundColor: theme.palette.text.hint,
    borderRadius: theme.spacing(0.5),
    height: theme.spacing(1),
    animation: `$fadeInAndOut 500ms infinite ${theme.transitions.easing.easeInOut} alternate`,
  },
  toolbarActions: {
    display: 'flex',
  },
  headerContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(0.5),
    '& > svg': {
      fontSize: theme.spacing(2),
    },
    '& > * + *': {
      marginLeft: theme.spacing(0.5),
    },
  },
  visibleOnRowHover: {
    visibility: 'hidden',
    '$tableRow:hover &': {
      visibility: 'visible',
    },
  },
  childBackground: {
    backgroundColor: theme.palette.grey[100],
  },
  parentShadow: {
    boxShadow: `0 8px 6px -6px ${theme.palette.grey[400]}`,
    zIndex: 1,
  },
  childColumnWidth: {
    width: theme.spacing(2),
  },
  cellIconSpacing: {
    marginRight: theme.spacing(1),
  },
  ...hoverContainerStyles,
}))(VirtualizedTable);
